import { MAX_BYLINE_CONNECTION_ICONS } from '../utils/getBylineConnectionIcons.ts';
import type { BylineSmartLinksQuery$data } from '../queries/__generated__/BylineSmartLinksQuery.graphql.ts';

export type OutboundBylineSsrStatus = 'present' | 'empty' | 'unavailable';

// A positive connection signal reserves a fixed three-icon footprint (CCINT-9354). `linkCount` is
// the number of sampled Smart Links (at most `MAX_BYLINE_CONNECTION_ICONS`, the query's `first`),
// which `shouldPaintBylineSsrPlaceholder` adds to the inbound and related samples to decide whether
// the page reaches the byline's row minimum. CCINT-9359
export type OutboundBylineSsrState = Readonly<
	| { contentId: string; status: 'present'; iconCount: number; linkCount: number }
	| { contentId: string; status: 'empty' | 'unavailable' }
>;

export function createOutboundBylineSsrState(
	contentId: string,
	status: 'empty' | 'unavailable',
): OutboundBylineSsrState;
export function createOutboundBylineSsrState(
	contentId: string,
	status: 'present',
	linkCount: number,
): OutboundBylineSsrState;
export function createOutboundBylineSsrState(
	contentId: string,
	status: OutboundBylineSsrStatus,
	linkCount?: number,
): OutboundBylineSsrState {
	if (status === 'present') {
		return Object.freeze({
			contentId,
			status,
			iconCount: MAX_BYLINE_CONNECTION_ICONS,
			linkCount: linkCount ?? 0,
		});
	}
	return Object.freeze({ contentId, status });
}

// Shared by the loading placeholder and the loaded byline, so both reserve alike.
export const getReservedBylineIconCount = (
	outboundState: OutboundBylineSsrState | undefined,
	hasDevDocsConnection: boolean,
	hasIncomingConnection = false,
): number => {
	// Unavailable or missing data does not establish a connection. Keep empty pages hidden.
	return outboundState?.status === 'present' || hasDevDocsConnection || hasIncomingConnection
		? MAX_BYLINE_CONNECTION_ICONS
		: 0;
};

export const normalizeOutboundBylineSsrState = (
	contentId: string,
	data: BylineSmartLinksQuery$data | null | undefined,
): OutboundBylineSsrState => {
	const nodes = data?.contentSmartLinks?.nodes;
	if (nodes === null || nodes === undefined) {
		return createOutboundBylineSsrState(contentId, 'unavailable');
	}

	// Malformed data does not establish a reliable connection signal.
	const hasMalformedNode = nodes.some(
		(node) => typeof node?.url !== 'string' || node.url.length === 0,
	);
	if (hasMalformedNode) {
		return createOutboundBylineSsrState(contentId, 'unavailable');
	}

	if (nodes.length === 0) {
		return createOutboundBylineSsrState(contentId, 'empty');
	}

	return createOutboundBylineSsrState(contentId, 'present', nodes.length);
};
